<script setup>
// A link to a part in ANOTHER area, shown as a small chip beside the hovered or
// selected card (and in the dialog) instead of a long line across the map: the
// other area's tint, the part's illustration, its name and its area. Clicking
// flies to that part.
import SystemMapArt from '~/components/systemMap/SystemMapArt.vue';

const props = defineProps({
  // From crossAreaLinks: {id, label, card, reverse, node, section, tone}
  link: {type: Object, required: true},
  // 'sm' beside a card on a desktop, 'md' in the dialog and on a phone (44px target)
  size: {type: String, default: 'sm'},
  // The part this chip hangs off, for the relation sentence in the tooltip
  fromName: {type: String, default: ''},
});
const emit = defineEmits(['jump']);

// A few labels read better as a phrase in a sentence; a link may also carry its
// own sentence (`say`) when neither direction reads well as subject-verb-object
const PHRASES = Object.freeze({gates: 'gates entry to', 'is a': 'is a kind of', like: 'works like'});
const tidy = (s) => s.replace(/\s+/g, ' ').trim();
// "Archive archives Box league" reads oddly: when the subject is the verb's own
// stem the sentence is written "Archive: archives Box league" instead.
const stemmed = (subject, label) => {
  const stem = (subject || '').toLowerCase().replace(/s$/, '');
  return !!stem && (label || '').toLowerCase().split(' ')[0].startsWith(stem);
};
const sentence = computed(() => {
  const l = props.link;
  const other = l.node?.name || l.id;
  const from = props.fromName || 'This';
  if (l.kind === 'step') return l.reverse ? `${other} comes after ${from}` : `${other} is needed before ${from}`;
  if (l.say) return l.say;
  const label = PHRASES[l.label] || l.label;
  const subject = l.reverse ? other : from;
  const object = l.reverse ? from : other;
  return tidy(`${subject}${stemmed(subject, label) ? ':' : ''} ${label} ${object}`);
});
// The second line: the step's direction, then the area; a part named like its
// area ("Club" in Club) shows the name once, and its "Go to" says it once too (R51 L5)
const areaName = computed(() => (props.link.section?.name && props.link.section.name !== props.link.node?.name ? props.link.section.name : ''));
const areaLine = computed(() => {
  const l = props.link;
  const area = areaName.value;
  if (l.kind === 'step') return area ? `${l.reverse ? 'Comes next' : 'Needs first'} · ${area}` : (l.reverse ? 'Comes next' : 'Needs first');
  return area;
});
const goTo = computed(() => `Go to ${props.link.node?.name || props.link.id}${areaName.value ? ` in ${areaName.value}` : ''}.`);
</script>

<template>
  <v-tooltip :text="sentence" location="top" :open-delay="350">
    <template #activator="{props: tip}">
      <button
          v-bind="tip"
          type="button"
          class="smc"
          :class="[`smc--${size}`, {'smc--step': link.kind === 'step'}]"
          :style="{'--chip-tint': link.tone.tint, '--chip-ink': link.tone.ink}"
          :aria-label="`${sentence}. ${goTo}`"
          @click.stop="emit('jump', link.id)"
      >
        <span class="smc-ic"><system-map-art :name="link.node?.art || 'grid'" :size="size === 'sm' ? 16 : 20"/></span>
        <span class="smc-text">
          <span class="smc-name">{{ link.node?.name }}</span>
          <span v-if="areaLine" class="smc-area">{{ areaLine }}</span>
        </span>
        <v-icon :size="size === 'sm' ? 14 : 16" class="smc-arrow">mdi-arrow-right</v-icon>
      </button>
    </template>
  </v-tooltip>
</template>

<style scoped lang="scss">
.smc {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px 4px 4px;
  min-height: 34px;
  max-width: 100%;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--chip-ink) 28%, transparent);
  background: #fff;
  color: rgb(var(--v-theme-text));
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(26, 26, 26, 0.06), 0 10px 24px -16px rgba(26, 26, 26, 0.3);
  transition: transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease;

  &:hover,
  &:focus-visible {
    background: var(--chip-tint);
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(26, 26, 26, 0.08), 0 14px 28px -16px rgba(26, 26, 26, 0.35);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }

  &--md {
    min-height: 44px;
    padding: 6px 14px 6px 6px;
    gap: 10px;
  }

  &--step {
    border-style: dashed;
  }
}

.smc-ic {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--chip-tint);
  color: var(--chip-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  :deep(.sma) {
    color: var(--chip-ink);
  }

  :deep(.sma-a) {
    stroke: var(--chip-ink);
    fill: #fff;
  }

  :deep(.sma-f) {
    fill: var(--chip-ink);
  }
}

.smc--md .smc-ic {
  width: 32px;
  height: 32px;
}

.smc-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.15;
}

.smc-name {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: -0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.smc--md .smc-name {
  font-size: 13.5px;
}

.smc-area {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--chip-ink);
  white-space: nowrap;
}

.smc--md .smc-area {
  font-size: 11.5px;
}

.smc-arrow {
  color: var(--chip-ink);
  margin-left: 2px;
  flex: none;
}

@media (prefers-reduced-motion: reduce) {
  .smc { transition: none; }
  .smc:hover { transform: none; }
}
</style>
